import moment from "moment";
import { Badge, Flex, Icon, Small, Text } from "orcs-design-system";

import {
  formatElapsedAgo,
  formatElapsedOverdue,
  liveElapsedHours,
  useClockNow,
} from "src/pages/WorkforceGuardrails/clockHours";

import InboxCardShell from "../InboxCardShell";
import { lastSubmittedLine, type ForecastSubmissionItem } from "./item";

const HOUR_MS = 3_600_000;
const DUE_LABEL = "D MMM";

type DueMeta = { label: string; overdue: boolean };

const dueMeta = (dueAt: string | null, nowMs: number): DueMeta | null => {
  if (dueAt == null || dueAt === "") {
    return null;
  }
  const dueMs = Date.parse(dueAt);
  if (!Number.isFinite(dueMs)) {
    return null;
  }
  const overdueHours = (nowMs - dueMs) / HOUR_MS;
  if (overdueHours > 0) {
    return {
      label:
        formatElapsedOverdue(overdueHours) ??
        `Due ${moment(dueAt).format(DUE_LABEL)}`,
      overdue: true,
    };
  }
  return { label: `Due ${moment(dueAt).format(DUE_LABEL)}`, overdue: false };
};

const ForecastInboxCard = ({
  item,
  selected,
  arrived = false,
  onSelect,
}: {
  item: ForecastSubmissionItem;
  selected: boolean;
  arrived?: boolean;
  onSelect: (key: string) => void;
}) => {
  const nowMs = useClockNow();
  const due = dueMeta(item.dueAt, nowMs);
  const previous = lastSubmittedLine(item.lastSubmittedFor);
  return (
    <InboxCardShell
      itemKey={item.key}
      selected={selected}
      arrived={arrived}
      onSelect={onSelect}
      kind={
        <Flex alignItems="center" gap="xs" flexWrap="wrap">
          <Text>Team</Text>
          <Badge variant="secondary">Forecast</Badge>
        </Flex>
      }
      meta={
        <>
          <Small color="greyDark">
            <Icon icon={["far", "clock"]} mr="xs" />
            {formatElapsedAgo(liveElapsedHours(null, item.since, nowMs))}
          </Small>
          {due ? (
            <Small color={due.overdue ? "danger" : "greyDark"}>
              <Icon
                icon={["far", due.overdue ? "stopwatch" : "calendar"]}
                {...(due.overdue ? { color: "danger" } : {})}
                mr="xs"
                aria-hidden="true"
              />
              <span>{due.label}</span>
            </Small>
          ) : null}
        </>
      }
    >
      <Text weight="bold" display="block">
        {item.title}
      </Text>
      {item.subtitle ? (
        <Small color="greyDark" display="block">
          {item.subtitle}
        </Small>
      ) : null}
      <Small display="block" mt="xs">
        {item.owedAs}
      </Small>
      {previous ? <Small display="block">{previous}</Small> : null}
    </InboxCardShell>
  );
};

export default ForecastInboxCard;
